import { useEffect, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { initAnalytics } from "@/lib/analytics";

const KEY = "kupish:cookie-consent";

export function CookieConsent() {
  const [show, setShow] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const acceptRef = useRef<HTMLButtonElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  useEffect(() => {
    try {
      const v = localStorage.getItem(KEY);
      if (!v) setShow(true);
      else if (v === "accept") initAnalytics();
    } catch {}
  }, []);

  useEffect(() => {
    if (!show) return;
    previouslyFocused.current = (document.activeElement as HTMLElement) ?? null;
    // Move focus into dialog after mount
    const t = window.setTimeout(() => acceptRef.current?.focus(), 0);

    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      const root = containerRef.current;
      if (!root) return;
      const focusables = root.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener("keydown", onKey);
      previouslyFocused.current?.focus?.();
    };
  }, [show]);

  const decide = (v: "accept" | "reject") => {
    try { localStorage.setItem(KEY, v); } catch {}
    if (v === "accept") initAnalytics();
    setShow(false);
  };

  if (!show) return null;
  return (
    <div
      ref={containerRef}
      role="dialog"
      aria-modal="false"
      aria-labelledby="cookie-consent-title"
      aria-describedby="cookie-consent-desc"
      className="fixed inset-x-0 bottom-0 z-50 px-3 pb-3 md:px-4 md:pb-4"
    >
      <div className="mx-auto max-h-[45vh] max-w-4xl overflow-auto bg-background border border-border shadow-lg p-4 md:p-6 flex flex-col md:flex-row items-start md:items-center gap-3 md:gap-4">
        <h2 id="cookie-consent-title" className="sr-only">Obvestilo o piškotkih</h2>
        <p id="cookie-consent-desc" className="text-xs leading-relaxed text-foreground md:text-sm flex-1">
          Uporabljamo nujne piškotke za delovanje strani in (po vaši privolitvi) anonimno analitiko. Več v{" "}
          <Link to="/piskotki" className="underline hover:text-accent">Pravilniku o piškotkih</Link>.
        </p>
        <div className="grid w-full grid-cols-2 gap-2 shrink-0 md:w-auto md:flex">
          <button onClick={() => decide("reject")} className="border border-border px-3 py-2 text-xs hover:bg-muted md:px-4 md:text-sm">
            Samo nujni
          </button>
          <button ref={acceptRef} onClick={() => decide("accept")} className="bg-primary text-primary-foreground px-3 py-2 text-xs hover:opacity-90 md:px-4 md:text-sm">
            Sprejmem vse
          </button>
        </div>
      </div>
    </div>
  );
}
